import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import { getRoleLabel } from 'utils/roleLabels';

import { IconLogout } from '@tabler/icons-react';

function getInitials(nameOrEmail?: string) {
  if (!nameOrEmail) {
    return '?';
  }

  const parts = nameOrEmail.split(/[\s@.]+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
}

export default function ProfileSection() {
  const theme = useTheme();
  const {
    state: { borderRadius }
  } = useConfig();
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const roles = useMemo(() => user?.roles ?? [], [user?.roles]);
  const roleText = roles.length > 0 ? roles.map(getRoleLabel).join(', ') : 'Nincs szerepkör';
  const displayEmail = user?.email ?? '-';

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        sx={{ cursor: 'pointer', minWidth: { xs: 'auto', sm: 260 }, justifyContent: 'flex-end' }}
      >
        <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: 'text.primary' }}>
            {displayEmail}
          </Typography>
          <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
            {roleText}
          </Typography>
        </Box>
        <Avatar
          alt={user?.name || user?.email || 'User'}
          sx={{
            width: 34,
            height: 34,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontSize: '0.85rem',
            fontWeight: 600
          }}
          aria-label="user-account"
        >
          {getInitials(user?.name || user?.email)}
        </Avatar>
      </Stack>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 14] } }]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard border={false} elevation={8} content={false} boxShadow shadow={theme.shadows[8]}>
                    <Box sx={{ p: 2, pb: 1.5, maxWidth: 360 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {user?.name || displayEmail}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                        {displayEmail}
                      </Typography>
                      <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {roles.length > 0 ? (
                          roles.map((role) => <Chip key={role} label={getRoleLabel(role)} size="small" variant="outlined" />)
                        ) : (
                          <Chip label="Nincs szerepkör" size="small" variant="outlined" />
                        )}
                      </Stack>
                    </Box>
                    <Divider />
                    <Box sx={{ p: 1 }}>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          minWidth: 300,
                          borderRadius: `${borderRadius}px`,
                          '& .MuiListItemButton-root': { mt: 0.5 }
                        }}
                      >
                        <ListItemButton sx={{ borderRadius: `${borderRadius}px` }} onClick={handleLogout}>
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Kijelentkezés</Typography>} />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
